import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { getPosts, deletePost } from '../../api/news';
import NewsPostDialog from '../../components/news/NewsPostDialog';

const NewsManagement = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPosts, setTotalPosts] = useState(0);
  const [selectedRole, setSelectedRole] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentPost, setCurrentPost] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [postToDelete, setPostToDelete] = useState(null);
  
  const pageSize = 10;
  
  useEffect(() => {
    fetchPosts();
  }, [page, selectedRole]);
  
  const fetchPosts = async () => {
    setLoading(true);
    try {
      const params = {
        skip: (page - 1) * pageSize,
        limit: pageSize
      };
      
      if (selectedRole) {
        params.roleId = selectedRole;
      }
      
      const result = await getPosts(params);
      setPosts(result.items);
      setTotalPosts(result.total);
    } catch (err) {
      console.error('Error fetching posts:', err);
      setError('Failed to load news posts. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };
  
  const handleRoleFilterChange = (event) => {
    setSelectedRole(event.target.value);
    setPage(1); // Reset to first page on filter change
  };
  
  const handleAddPost = () => {
    setCurrentPost(null);
    setDialogOpen(true);
  };
  
  const handleEditPost = (post) => {
    setCurrentPost(post);
    setDialogOpen(true);
  };
  
  const handleDeleteClick = (post) => {
    setPostToDelete(post);
    setDeleteDialogOpen(true);
  };
  
  const handleDeleteConfirm = async () => {
    if (!postToDelete) return;
    
    try {
      await deletePost(postToDelete.id);
      setDeleteDialogOpen(false);
      fetchPosts(); // Refresh the list
    } catch (err) {
      console.error('Error deleting post:', err);
      setError('Failed to delete post. Please try again.');
    }
  };
  
  const handleDialogClose = (refresh = false) => {
    setDialogOpen(false);
    if (refresh) {
      fetchPosts();
    }
  };
  
  return (
    <div className="container mx-auto px-4">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-ui-light-text-primary dark:text-ui-dark-text-primary mb-2">
          Manage News & Announcements
        </h1>
        <p className="text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
          Create and manage news posts for different user roles
        </p>
      </div>
      
      <div className="bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow-md p-6 mb-8">
        <div className="flex justify-between items-center mb-6">
          <div className="min-w-[200px]">
            <label htmlFor="role-filter" className="block text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary mb-1">
              Filter by Role
            </label>
            <select
              id="role-filter"
              value={selectedRole || ''}
              onChange={handleRoleFilterChange}
              className="w-full px-3 py-2 text-ui-light-text-primary dark:text-ui-dark-text-primary bg-ui-light-card dark:bg-ui-dark-card border border-ui-light-border dark:border-ui-dark-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-offset-ui-dark-background transition-colors"
            >
              <option value="">All Roles</option>
              {/* Assume we'll get roles from context in a real app */}
              <option value="1">Astronomers</option>
              <option value="2">Data Analysts</option>
              <option value="3">Public Users</option>
            </select>
          </div>
          
          <button 
            className="px-4 py-2 text-sm font-medium text-white dark:text-ui-dark-text-primary bg-primary-600 dark:bg-primary-700 rounded-md hover:bg-primary-700 dark:hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-ui-dark-background transition-colors flex items-center"
            onClick={handleAddPost}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
            Create News Post
          </button>
        </div>
        
        {error && (
          <div className="p-4 mb-6 text-status-error-light-text dark:text-status-error-dark-text bg-status-error-light-bg dark:bg-status-error-dark-bg rounded-md border border-status-error-light-text/20 dark:border-status-error-dark-text/20">
            {error}
          </div>
        )}
        
        <div className="overflow-x-auto border border-ui-light-border dark:border-ui-dark-border rounded-md">
          <table className="min-w-full divide-y divide-ui-light-border dark:divide-ui-dark-border">
            <thead className="bg-ui-light-hover dark:bg-ui-dark-hover">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary uppercase tracking-wider">
                  Title
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary uppercase tracking-wider">
                  Roles
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary uppercase tracking-wider">
                  Published
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary uppercase tracking-wider">
                  Author
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary uppercase tracking-wider w-[100px]">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-ui-light-card dark:bg-ui-dark-card divide-y divide-ui-light-border dark:divide-ui-dark-border">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-4 text-center">
                    <svg className="inline-block w-6 h-6 text-primary-600 dark:text-primary-400 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </td>
                </tr>
              ) : posts.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-4 text-center text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
                    No news posts found
                  </td>
                </tr>
              ) : (
                posts.map(post => (
                  <tr key={post.id} className="hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {post.is_pinned && (
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primary-600 dark:text-primary-400 mr-2" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M5 5a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1zm4 3a1 1 0 00-1 1v6a1 1 0 102 0V9a1 1 0 00-1-1zm6-3a1 1 0 100-2h-3a1 1 0 000 2h3z" />
                          </svg>
                        )}
                        <span className="text-ui-light-text-primary dark:text-ui-dark-text-primary">
                          {post.title}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-wrap">
                        {post.roles.map(role => (
                          <span 
                            key={role.id}
                            className="px-2 py-1 text-xs font-medium rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 mr-1 mb-1"
                          >
                            {role.name}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-ui-light-text-secondary dark:text-ui-dark-text-secondary">
                      {format(new Date(post.published_at), 'MMM d, yyyy')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-ui-light-text-secondary dark:text-ui-dark-text-secondary">
                      {post.author_name || 'Unknown'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        className="p-1 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 rounded-full hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover transition-colors mr-2"
                        onClick={() => handleEditPost(post)}
                        title="Edit"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                        </svg>
                      </button>
                      <button
                        className="p-1 text-status-error-light-text dark:text-status-error-dark-text hover:text-status-error-light-text/80 dark:hover:text-status-error-dark-text/80 rounded-full hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover transition-colors"
                        onClick={() => handleDeleteClick(post)}
                        title="Delete"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {totalPosts > pageSize && (
          <div className="flex justify-center mt-6">
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
              <button
                onClick={() => handlePageChange(1)}
                disabled={page === 1}
                className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-ui-light-border dark:border-ui-dark-border bg-ui-light-card dark:bg-ui-dark-card text-sm font-medium text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover disabled:opacity-50"
              >
                <span className="sr-only">First</span>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M15.707 15.707a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414l5-5a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 010 1.414zm-6 0a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414l5-5a1 1 0 011.414 1.414L5.414 10l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                </svg>
              </button>
              <button
                onClick={() => handlePageChange(Math.max(1, page - 1))}
                disabled={page === 1}
                className="relative inline-flex items-center px-2 py-2 border border-ui-light-border dark:border-ui-dark-border bg-ui-light-card dark:bg-ui-dark-card text-sm font-medium text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover disabled:opacity-50"
              >
                <span className="sr-only">Previous</span>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </button>
              
              {/* Page numbers */}
              {[...Array(Math.min(5, Math.ceil(totalPosts / pageSize)))].map((_, index) => {
                let pageNum;
                
                // Calculate which page numbers to show
                if (Math.ceil(totalPosts / pageSize) <= 5) {
                  pageNum = index + 1;
                } else if (page <= 3) {
                  pageNum = index + 1;
                } else if (page >= Math.ceil(totalPosts / pageSize) - 2) {
                  pageNum = Math.ceil(totalPosts / pageSize) - 4 + index;
                } else {
                  pageNum = page - 2 + index;
                }
                
                if (pageNum <= Math.ceil(totalPosts / pageSize)) {
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        page === pageNum 
                          ? 'z-10 bg-primary-50 dark:bg-primary-900/30 border-primary-500 dark:border-primary-700 text-primary-600 dark:text-primary-400' 
                          : 'bg-ui-light-card dark:bg-ui-dark-card border-ui-light-border dark:border-ui-dark-border text-ui-light-text-secondary dark:text-ui-dark-text-secondary hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                }
                
                return null;
              })}
              
              <button
                onClick={() => handlePageChange(Math.min(Math.ceil(totalPosts / pageSize), page + 1))}
                disabled={page === Math.ceil(totalPosts / pageSize)}
                className="relative inline-flex items-center px-2 py-2 border border-ui-light-border dark:border-ui-dark-border bg-ui-light-card dark:bg-ui-dark-card text-sm font-medium text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover disabled:opacity-50"
              >
                <span className="sr-only">Next</span>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              </button>
              <button
                onClick={() => handlePageChange(Math.ceil(totalPosts / pageSize))}
                disabled={page === Math.ceil(totalPosts / pageSize)}
                className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-ui-light-border dark:border-ui-dark-border bg-ui-light-card dark:bg-ui-dark-card text-sm font-medium text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover disabled:opacity-50"
              >
                <span className="sr-only">Last</span>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10.293 15.707a1 1 0 010-1.414L14.586 10l-4.293-4.293a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  <path fillRule="evenodd" d="M4.293 15.707a1 1 0 010-1.414L8.586 10 4.293 5.707a1 1 0 011.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              </button>
            </nav>
          </div>
        )}
      </div>
      
      {/* Post dialog */}
      <NewsPostDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        post={currentPost}
      />
      
      {/* Delete confirmation dialog */}
      {deleteDialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow-lg max-w-md w-full">
            <div className="p-6">
              <h3 className="text-lg font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary mb-4">
                Confirm Deletion
              </h3>
              <p className="text-ui-light-text-secondary dark:text-ui-dark-text-secondary mb-6">
                Are you sure you want to delete the post "{postToDelete?.title}"? This action cannot be undone.
              </p>
              <div className="flex justify-end space-x-2">
                <button 
                  onClick={() => setDeleteDialogOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary border border-ui-light-border dark:border-ui-dark-border rounded-md hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-offset-ui-dark-background transition-colors"
                >
                  Cancel
                </button>
                <button 
                  onClick={handleDeleteConfirm}
                  className="px-4 py-2 text-sm font-medium text-white dark:text-status-error-dark-text bg-status-error-light-text dark:bg-status-error-dark-bg rounded-md hover:bg-status-error-light-text/90 dark:hover:bg-status-error-dark-bg/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-status-error-light-text dark:focus:ring-offset-ui-dark-background transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsManagement;